import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Copy,
  Check,
  Mail,
  Link as LinkIcon,
  X,
  Send,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';

import type { GlobalDialogProps } from '../../../types';
import { RootState } from '../../../reducers';
import { getJoinUrlThunk } from '../teamSlice';
import { getJoinUrl } from '../../../lib/routingUtils';
import { usePlan } from '../../plan/hooks/plan';
import { sendEmail } from '../../../services/emailService';
import emailTemplates from '../../email/templates';
import ContactSalesForm from '../../sales/ContactSalesForm';
import { planPricesPerMonth } from '../../../constants/prices';

export const InviteLinkDialog = ({ onClose }: GlobalDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { team, plan } = useSelector((state: RootState) => state);
  const { teamId, joinCode, name: teamName } = team?.selectedTeam || {};
  const [inviteLink, setInviteLink] = useState('');
  const { isPlan, teamPlan } = usePlan();
  const { term } = plan.subscription || {};
  const isPaidUser = isPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailError, setEmailError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);

  useEffect(() => {
    if (!joinCode && teamId) {
      dispatch(getJoinUrlThunk(teamId));
    }
  }, [dispatch, joinCode, teamId]);

  useEffect(() => {
    if (joinCode) {
      setInviteLink(getJoinUrl(joinCode));
    }
  }, [joinCode]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast(t('invite.link.linkCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const getPricePerLicense = () => {
    if (!teamPlan || !(teamPlan in planPricesPerMonth)) {
      return '';
    }

    if (term === 'monthly') {
      return `$${
        planPricesPerMonth[teamPlan as keyof typeof planPricesPerMonth].monthly
      }/month`;
    }

    const annualPrice =
      planPricesPerMonth[teamPlan as keyof typeof planPricesPerMonth].annual *
      12;
    return `$${annualPrice}/year`;
  };

  const validateEmail = (input: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(input.toLowerCase());
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    const emails = pastedText
      .split(/[,;\n\s]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const validEmails = emails
      .filter((email) => validateEmail(email))
      .filter((email) => !emailList.includes(email));

    if (validEmails.length > 0) {
      setEmailList([...emailList, ...validEmails]);
      setEmailInput('');
      setEmailError('');
    }

    if (validEmails.length < emails.length) {
      setEmailError(t('invite.email.errors.invalidPasted'));
    }
  };

  const handleAddEmail = () => {
    if (!emailInput) {
      setEmailError(t('invite.email.errors.required'));
      return;
    }
    if (!validateEmail(emailInput)) {
      setEmailError(t('invite.email.errors.invalid'));
      return;
    }
    if (emailList.includes(emailInput)) {
      setEmailError(t('invite.email.errors.duplicate'));
      return;
    }
    setEmailList([...emailList, emailInput]);
    setEmailInput('');
    setEmailError('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailList(emailList.filter((email) => email !== emailToRemove));
  };

  const handleSendInvites = async () => {
    try {
      if (!joinCode) {
        toast.error(t('invite.email.unableToGenerate'));
        return;
      }

      setIsSending(true);
      const inviteUrl = getJoinUrl(joinCode);

      await Promise.all(
        emailList.map((email) =>
          sendEmail({
            template: 'genericEmail',
            toEmail: email,
            data: emailTemplates.teamInvite({
              teamName: teamName || 'a team',
              inviteUrl,
            }),
          }),
        ),
      );

      toast(t('invite.email.invitationsSent', { count: emailList.length }));
      setEmailList([]);
    } catch (error) {
      console.error('Failed to send invitations:', error);
      toast.error(t('invite.email.sendFailed'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={() => onClose?.()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl font-semibold">
              {t('invite.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('invite.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            {isPaidUser && (
              <div
                className={`transition-all duration-300 ease-in-out ${
                  hasAcknowledged
                    ? 'mb-0 max-h-0 overflow-hidden opacity-0'
                    : 'mb-6 max-h-32 opacity-100'
                }`}
              >
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <div className="flex items-center justify-between">
                    <AlertDescription className="text-sm">
                      {t('invite.billing.notice')}{' '}
                      <span className="font-medium">
                        ({getPricePerLicense()} {t('invite.billing.perLicense')}
                        )
                      </span>
                    </AlertDescription>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHasAcknowledged(true)}
                      className="ml-4 h-7 px-3 text-xs"
                    >
                      {t('invite.billing.understand')}
                    </Button>
                  </div>
                </Alert>
              </div>
            )}

            {(!isPaidUser || hasAcknowledged) && (
              <Tabs defaultValue="email" className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-10">
                  <TabsTrigger
                    value="email"
                    className="data-[state=active]:bg-background"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    <span className="text-sm">{t('invite.tabs.email')}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="link"
                    className="data-[state=active]:bg-background"
                  >
                    <LinkIcon className="mr-2 h-4 w-4" />
                    <span className="text-sm">{t('invite.tabs.link')}</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="email" className="mt-6 space-y-4">
                  <div className="space-y-3">
                    <Label htmlFor="email" className="text-sm font-medium">
                      {t('invite.email.title')}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="email"
                        type="email"
                        placeholder={t('invite.email.placeholder')}
                        value={emailInput}
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          setEmailError('');
                        }}
                        onPaste={handlePaste}
                        onKeyPress={handleKeyPress}
                        disabled={isPaidUser && !hasAcknowledged}
                        className="h-10"
                      />
                      <Button
                        onClick={handleAddEmail}
                        disabled={
                          !emailInput || (isPaidUser && !hasAcknowledged)
                        }
                        size="default"
                        className="h-10 px-4"
                      >
                        {t('invite.email.add')}
                      </Button>
                    </div>
                    {emailError && (
                      <p className="text-xs text-destructive mt-1">
                        {emailError}
                      </p>
                    )}
                  </div>

                  {emailList.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-1.5">
                        {emailList.map((email) => (
                          <Badge
                            key={email}
                            variant="secondary"
                            className="pl-2 pr-0.5 py-0.5 text-xs font-normal"
                          >
                            {email}
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail(email)}
                              className="ml-1 p-0.5 rounded-sm opacity-60 hover:opacity-100 transition-opacity"
                              disabled={isPaidUser && !hasAcknowledged}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>

                      <div>
                        <Button
                          onClick={handleSendInvites}
                          disabled={
                            (isPaidUser && !hasAcknowledged) || isSending
                          }
                          className="h-9"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          {isSending
                            ? t('invite.email.sending')
                            : t('invite.email.sendInvitations', {
                                count: emailList.length,
                              })}
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="link" className="mt-6 space-y-4">
                  <div className="space-y-3">
                    <Label htmlFor="link" className="text-sm font-medium">
                      {t('invite.link.title')}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="link"
                        value={inviteLink}
                        readOnly
                        disabled={isPaidUser && !hasAcknowledged}
                        className="h-10 text-sm"
                      />
                      <Button
                        onClick={handleCopyLink}
                        disabled={isPaidUser && !hasAcknowledged}
                        size="icon"
                        variant={copied ? 'default' : 'outline'}
                        className="h-10 w-10 shrink-0"
                      >
                        {copied ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('invite.link.description')}
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            )}

            <Separator className="my-6" />

            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSalesDialogOpen(true)}
                className="h-9 px-4 text-muted-foreground hover:text-foreground"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                <span className="text-sm">{t('invite.enquire')}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={salesDialogOpen} onOpenChange={setSalesDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('invite.sales.title')}</DialogTitle>
          </DialogHeader>
          <ContactSalesForm formSource="Team Invite - Enquire Team Plan" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InviteLinkDialog;
